// src/features/admin/AuditLogTab.tsx
// AdminService's own unified, cross-service audit log — a compact filterable
// browser. Distinct from the compliance-grade audittrail explorer (shortcut
// linked from the Overview tab). ListAuditLogs, ListAuditLogsByActor,
// ListAuditLogsByResource, GetAuditLog, ExportAuditLogs.

import { useCallback, useEffect, useState } from 'react';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { adminStore } from '@core/stores';
import { Button, Input, Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableEmpty, ErrorAlert } from '@react/components';
import { Select } from '@/components/Select';
import { ListAuditLogsByActor } from '@core/api/endpoints/admin/list_audit_logs_by_actor';
import { ListAuditLogsByResource } from '@core/api/endpoints/admin/list_audit_logs_by_resource';
import { ExportAuditLogs } from '@core/api/endpoints/admin/export_audit_logs';
import type { AuditLogEntry } from '@core/types/admin';
import type { ExportFormat } from '@core/types/admin';
import { AuditLogDetailRail } from './AuditLogDetailRail';
import { AUDIT_ACTION_VALUES, enumKey, errMessage, paginationParams } from './logic';

export const AuditLogTab = observer(() => {
  const { t } = useI18n();
  const [items, setItems] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actorId, setActorId] = useState('');
  const [resourceType, setResourceType] = useState('');
  const [resourceId, setResourceId] = useState('');
  const [action, setAction] = useState('');
  const [selected, setSelected] = useState<AuditLogEntry | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [exportFormat, setExportFormat] = useState<ExportFormat>('EXPORT_FORMAT_CSV');
  const [exporting, setExporting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    // ListAuditLogsByActor/ByResource/ListAuditLogs only accept pagination on
    // this endpoint — action is applied client-side over the fetched page so
    // every filter combination behaves consistently.
    const params = paginationParams(50);
    const req = resourceType.trim() && resourceId.trim()
      ? ListAuditLogsByResource(resourceType.trim(), resourceId.trim(), params)
      : actorId.trim()
        ? ListAuditLogsByActor(actorId.trim(), params)
        : adminStore.ListAuditLogs(params).then(() => ({ logs: { entries: adminStore.items } }));

    Promise.resolve(req)
      .then((res: { logs?: { entries?: AuditLogEntry[] } }) => {
        let rows = res.logs?.entries ?? [];
        if (action) rows = rows.filter((r) => r.action === action);
        setItems(rows);
      })
      .catch((e: unknown) => setError(errMessage(e, t('admin.auditLog.loadError'))))
      .finally(() => setLoading(false));
  }, [actorId, resourceType, resourceId, action, t]);

  useEffect(() => { load(); }, [load]);

  const openRow = async (row: AuditLogEntry) => {
    setSelected(row);
    if (!row.id) return;
    setDetailLoading(true);
    try {
      const res = await adminStore.GetAuditLog(row.id);
      setSelected(res);
    } catch {
      // Keep the row data already shown
    } finally {
      setDetailLoading(false);
    }
  };

  const runExport = async () => {
    setExporting(true);
    setError(null);
    try {
      const res = await ExportAuditLogs({
        query: {
          actor_id: actorId.trim() || undefined,
          resource_type: resourceType.trim() || undefined,
          resource_id: resourceId.trim() || undefined,
          action: (action || undefined) as AuditLogEntry['action'],
        },
        format: exportFormat,
      });
      if (res.download_url) window.open(res.download_url, '_blank', 'noopener');
    } catch (e) {
      setError(errMessage(e, t('admin.auditLog.exportError')));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="flex h-full gap-0">
      <div className="min-w-0 flex-1 space-y-4">
        <div className="flex flex-wrap items-end gap-2">
          <Input label={t('admin.auditLog.filter.actor')} value={actorId} onChange={(e) => setActorId(e.target.value)} className="w-44" fullWidth={false} />
          <Input label={t('admin.auditLog.filter.resourceType')} value={resourceType} onChange={(e) => setResourceType(e.target.value)} className="w-36" fullWidth={false} />
          <Input label={t('admin.auditLog.filter.resourceId')} value={resourceId} onChange={(e) => setResourceId(e.target.value)} className="w-44" fullWidth={false} />
          <Select
            label={t('admin.auditLog.filter.action')}
            value={action}
            onChange={(e) => setAction(e.target.value)}
            className="w-40"
            options={[{ value: '', label: t('common.all') }, ...AUDIT_ACTION_VALUES.map((a) => ({ value: a, label: t(`admin.auditAction.${enumKey(a, 'AUDIT_ACTION_')}`) }))]}
          />
          <Button size="sm" variant="secondary" onClick={load}>{t('common.filter')}</Button>
          <div className="ml-auto flex items-end gap-2">
            <Select
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value as ExportFormat)}
              className="w-28"
              options={[
                { value: 'EXPORT_FORMAT_CSV', label: 'CSV' },
                { value: 'EXPORT_FORMAT_JSON', label: 'JSON' },
                { value: 'EXPORT_FORMAT_PDF', label: 'PDF' },
              ]}
            />
            <Button size="sm" variant="secondary" onClick={runExport} isLoading={exporting}>{t('admin.auditLog.export')}</Button>
          </div>
        </div>

        {error && <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} />}

        <Table hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>{t('admin.auditLog.column.actor')}</TableHead>
              <TableHead>{t('admin.auditLog.column.action')}</TableHead>
              <TableHead>{t('admin.auditLog.column.resource')}</TableHead>
              <TableHead>{t('admin.auditLog.column.occurred')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={4} className="py-8 text-center text-muted-foreground">{t('common.loading')}</TableCell></TableRow>
            ) : items.length === 0 ? (
              <TableEmpty colSpan={4} message={t('admin.auditLog.empty')} />
            ) : (
              items.map((entry) => (
                <TableRow key={entry.id} onClick={() => openRow(entry)} className={selected?.id === entry.id ? 'bg-muted/50' : undefined}>
                  <TableCell className="text-sm">{entry.actor?.display_name || entry.actor?.entity?.id || '—'}</TableCell>
                  <TableCell className="text-sm">{t(`admin.auditAction.${enumKey(entry.action, 'AUDIT_ACTION_')}`)}</TableCell>
                  <TableCell className="text-sm">{entry.resource_type}{entry.resource?.id ? ` · ${entry.resource.id}` : ''}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{entry.occurred_at ? String(entry.occurred_at) : '—'}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {selected && !detailLoading && <AuditLogDetailRail entry={selected} onClose={() => setSelected(null)} />}
    </div>
  );
});

export default AuditLogTab;
